import { faFileAlt, faSquare } from "@fortawesome/free-regular-svg-icons";
import {
    faCalendar,
    faCheckSquare,
    faClipboard,
    faDotCircle,
    faFont,
    faHashtag,
    faICursor,
    faInfoCircle,
    faQuestionCircle,
    faTh,
    faThLarge,
    faThList,
    faUpload
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { QuestionTypes as qt } from "../common/constant";
import ObjectUtils, { coalesce } from "./ObjectUtils";

const {
    getDeepProperty,
    findByTemplate,
    opt
} = ObjectUtils;

export function isColumnIncomplete(column, questionAnswer, rows = questionAnswer.rows) {
    if (!column.required) return false;

    const answeredRows = new Set();
    for (const answer of coalesce(questionAnswer.answers, [])) {
        if (answer.colLabel === column.name && cleanText(answer.value).length) {
            answeredRows.add(answer.label);
        }
    }

    return coalesce(rows, []).some((row) => !answeredRows.has(row.name));
}

export const getRequiredColumns = (columns) => columns.filter(c => c.required);

export const getIncompleteColumns = (columns, questionAnswer, rows) =>
    columns.filter(c => isColumnIncomplete(c, questionAnswer, rows));

/**
 * Optimized check if a question is considered answered.
 * Handles thousands of answers efficiently by pre-indexing.
 */
export function isAnswered(questionModel, questionResult) {
    const answers = coalesce(getDeepProperty(questionResult, "answers"), []);
    const resultRows = coalesce(getDeepProperty(questionResult, "rows"), []);
    const columns = coalesce(getDeepProperty(questionModel, "columns"), []);
    const rows = coalesce(getDeepProperty(questionModel, "rows"), []);
    const type = questionModel.type;

    if (answers.length === 0) return false;

    switch (type) {
        case qt.text:
        case qt.date:
        case qt.number:
        case qt.comment: {
            const text = coalesce(answers[0]?.value, "");
            return realTextLength(text) > 0;
        }

        case qt.dropdown:
        case qt.radio:
        case qt.checkbox:
        case qt.predefinedList:
            return !!answers[0]?.value;

        case qt.matrixMultipleChoice:
        case qt.matrixSingleChoice:
            return rows.length === countOfAnsweredRows(answers);

        case qt.staticTable:
            return countOfAnsweredColsRows(answers) > 0 &&
                checkAllMandatoryColumnsFast(rows, columns, answers);

        case qt.dynamicTable:
            return countOfAnsweredColsRows(answers) > 0 &&
                checkAllMandatoryColumnsFast(resultRows, columns, answers);

        default:
            return false;
    }
}

export function hasMandatoryElements(questionModel, questionResult) {
    let result = false;
    if (qt.staticTable === questionModel.type) {
        const answers = coalesce(getDeepProperty(questionResult, "answers"), []);

        if (countOfAnsweredColsRows(answers) > 0) {
            const columns = coalesce(getDeepProperty(questionModel, "columns"), []);
            const rows = coalesce(getDeepProperty(questionModel, "rows"), []);
            const mandatoryCols = columns.filter((column) => column.required === true);
            const mandatoryRows = rows.filter((row) => row.required === true);
            result = mandatoryCols.length > 0 || mandatoryRows.length > 0;
        }
    } else if (qt.dynamicTable === questionModel.type) {
        const answers = coalesce(getDeepProperty(questionResult, "answers"), []);

        if (countOfAnsweredColsRows(answers) > 0) {
            const columns = coalesce(getDeepProperty(questionModel, "columns"), []);
            const mandatoryCols = columns.filter((column) => column.required === true);
            result = mandatoryCols.length > 0;
        }
    }

    return result;
}

export function realTextLength(text) {
    return String(text)
        .replace(/<\/?[a-zA-Z ]+\/?>/g, "")
        .replace(/&[a-zA-Z]+;/g, "*")
        .replace(/\s+/g, "")
        .length;
}

/**
 * ⚡ Fast regex-free text cleaning for performance.
 * Only use regex once if HTML-like tags are detected.
 */
export function cleanText(value = "") {
    if (!value || typeof value !== "string") return "";
    if (value.indexOf("<") === -1 && value.indexOf("&") === -1) {
        return value.trim();
    }

    const htmlWithSeparators = value
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<\/(?:p|div|li|ul|ol|h[1-6])\s*>/gi, " ");
    const parsedHtml = new DOMParser().parseFromString(htmlWithSeparators, "text/html");

    return (parsedHtml.body.textContent || "").replace(/\s+/g, " ").trim();
}

function countOfAnsweredRows(answers) {
    const unique = new Set();
    for (const a of answers) {
        const val = coalesce(a.value, "");
        if (val && realTextLength(val) > 0) unique.add(a.label);
    }
    return unique.size;
}

function countOfAnsweredColsRows(answers) {
    const unique = new Set();
    for (const a of answers) {
        const val = coalesce(a.value, "");
        if (val && realTextLength(val) > 0) {
            unique.add(`${a.label}::${a.colLabel}`);
        }
    }
    return unique.size;
}

function checkAllMandatoryColumnsFast(rows, columns, answers) {
    const mandatoryCols = new Set(columns.filter(c => c.required).map(c => c.name));
    const mandatoryRows = new Set(rows.filter(r => r.required).map(r => r.name));

    if (mandatoryCols.size === 0 && mandatoryRows.size === 0) return true;

    const answerMap = Object.create(null);
    for (const a of answers) {
        if (!a.label || !a.colLabel) continue;
        answerMap[a.label] ??= Object.create(null);
        answerMap[a.label][a.colLabel] = cleanText(a.value);
    }

    for (const rowName of mandatoryRows) {
        const rowAnswers = answerMap[rowName];
        if (!rowAnswers) return false;
        for (const colName of mandatoryCols) {
            if (!rowAnswers[colName]?.length) return false;
        }
    }

    for (const colName of mandatoryCols) {
        for (const rowName of rows.map(r => r.name)) {
            if (!answerMap[rowName]?.[colName]?.length) return false;
        }
    }

    return true;
}




/**
 * @returns first answer whitch match wit given params, or undefined if no one like it.
 */
export function findAnswer(collection, {
    row,
    column,
    value,
    name
}) {
    let rowName = getDeepProperty(row, "name");
    let colName = getDeepProperty(column, "name");
    let answers = findByTemplate(collection, {
        label: rowName,
        colLabel: colName,
        value: value,
        name
    });
    return getDeepProperty(answers, 0);
}

export function findSimpleAnswer(collection, {
    value,
    name
}) {
    let answers = findByTemplate(collection, {
        value: value,
        name
    });
    return getDeepProperty(answers, 0);
}



/**
 * @returns how many questions are of certan type.
 */
export function findNumberOfQuestionsByType(questions, type) {
    let numberOfQuestions = 0;
    for (let i = 0; i < questions.length; i++) {
        if (questions[i].type === type) {
            numberOfQuestions++;
        }
    }
    return numberOfQuestions;
}

export function makeAnswer(value, row, column, name) {
    let optionType = false;
    let option = {};
    if (column) {
        optionType =
            column.type === "Checkbox" ||
            column.type === "Radio" ||
            column.type === "Dropdown" ||
            column.type === "predefinedList";
        option = optionType && ObjectUtils.findByObjectProp(column.choices, "value", value);
    }
    return {
        value: value,
        label: opt(row).name,
        rowText: opt(row).text,
        rowKey: opt(row).key,
        colLabel: opt(column).name,
        colText: opt(column).text,
        columnType: opt(column).type,
        columnKey: opt(column).key,
        name: optionType ? coalesce(option.name, name) : name,
        text: optionType ? option.text : value,
        choiceKey: optionType ? coalesce(option.key, name) : null
    };
}

export function makeSimpleAnswer(value, question, name) {
    let optionType = isSimpleOptionType(question.type);
    let option = {};
    if (optionType) {
        option = ObjectUtils.findByObjectProp(question.choices, "value", value);
    }
    return {
        value: value,
        name: optionType ? coalesce(option.name, name) : name,
        text: optionType ? option.text : value,
        choiceKey: optionType ? coalesce(option.key, name) : null
    };
}

export function isSimpleType(type) {
    const simpleTypes = ["text", "comment", "radio", "checkbox", "date", "dropdown", "predefinedList", "number"];
    return simpleTypes.includes(type);
}

export function isSimpleOptionType(type) {
    const simpleTypes = ["radio", "checkbox", "dropdown", "predefinedList"];
    return simpleTypes.includes(type);
}

export function makeMatrixAnswer(value, row, choice) {
    return {
        value: value,
        label: opt(row).name,
        rowText: opt(row).text,
        rowKey: opt(row).key,
        name: value,
        text: choice.text
    };
}

export const QuestionIcon = (props) => {
    const {
        type,
        ...rest
    } = props;
    const {
        className,
        ...otherProps
    } = rest;

    if (type) {
        switch (type.toLowerCase().replace(/_/g, "")) {
            case "text":
                return (
                    <span className={`fa-layers ${className}`} {...otherProps}>
            <FontAwesomeIcon icon={faSquare}/>
            <FontAwesomeIcon
                icon={faFont}
                transform={{
                    size: 6,
                    x: -2
                }}
            />
            <FontAwesomeIcon
                icon={faICursor}
                transform={{
                    size: 6,
                    x: 3
                }}
            />
          </span>
                );
            case "comment":
                return <FontAwesomeIcon icon={faFileAlt} style={{minWidth: "0.85rem"}} {...rest} />;
            case "dropdown":
            case "predefinedlist":
            case "radio":
                return <FontAwesomeIcon icon={faDotCircle} {...rest} />;
            case "checkbox":
                return <FontAwesomeIcon icon={faCheckSquare} {...rest} />;
            case "number":
                return <FontAwesomeIcon icon={faHashtag} {...rest} />;
            case "date":
                return <FontAwesomeIcon icon={faCalendar} {...rest} />;
            case "dynamictable":
                return <FontAwesomeIcon icon={faThList} {...rest} />;
            case "statictable":
                return <FontAwesomeIcon icon={faThLarge} {...rest} />;
            case "matrixsinglechoice":
            case "matrixmultiplechoice":
                return <FontAwesomeIcon icon={faTh} {...rest} />;
            case "infomessage":
                return <FontAwesomeIcon icon={faInfoCircle} {...rest} />;
            case "upload":
                return <FontAwesomeIcon icon={faUpload} {...rest} />;
            case "projectdata":
                return <FontAwesomeIcon icon={faClipboard} {...rest} />;
            default:
                return <FontAwesomeIcon icon={faQuestionCircle} {...rest} />;
        }
    }

    return <FontAwesomeIcon icon={faQuestionCircle} {...rest} />;
};

